/**
 * Cursor arithmetic for the Quick Bytes carousel —
 * ROUTE_INVENTORY §2.12 #13 and #14.
 *
 * Extracted from `QuickBytesCarouselModal` so the navigation rules can be
 * table-tested. The modal's story asserts the cursor is WIRED to the tap
 * zones, chevrons and arrow keys; the cases live in `carouselCursor.test.ts`,
 * because "continuous navigation" is a claim about a dozen positions and a
 * play function can only walk a handful.
 *
 * ## Continuous means the deck has no seams
 *
 * A single Next moves through three different things depending on where you
 * are, and the user is meant to notice none of them:
 *
 *   within a concept   → the next card
 *   at a concept's end → the next concept, first card
 *   at the deck's end  → wrap to the very beginning
 *
 * Previous is the exact mirror, and the mirroring is the part worth testing:
 * stepping back into a previous concept must land on its **last** card, not its
 * first. Getting that wrong makes Prev-then-Next fail to return you where you
 * were, which is the one property a reader notices immediately.
 *
 * ## `fetch` is a third outcome, not an error
 *
 * At the loaded edge with more pages on the server, Next cannot resolve yet.
 * Rather than wrapping (which would silently teleport the reader to the start
 * of the deck when they expected to go forward), it reports that a page is
 * needed and names the index to land on once it arrives. The modal shows an
 * overlay meanwhile — §2.12 #14.
 */

export interface CarouselCursor {
  conceptIdx: number;
  cardIdx: number;
}

export type CursorAdvance =
  /** Resolved locally; move here. */
  | { kind: 'move'; cursor: CarouselCursor }
  /** At the loaded edge with more to come; fetch, then land on `target`. */
  | { kind: 'fetch'; target: number }
  /** Nothing to navigate. */
  | { kind: 'none' };

/** Only the shape the cursor needs: how many cards each concept has. */
type Deck = ReadonlyArray<{ cards: ReadonlyArray<unknown> }>;

const cardCount = (deck: Deck, idx: number) => deck[idx]?.cards.length ?? 0;

/**
 * Where Next goes from `cursor`.
 *
 * `hasMore` is the caller's `hasNextPage`. When it is false the deck is
 * complete and the end wraps; when true the end defers instead.
 */
export function advanceCursor(
  deck: Deck,
  cursor: CarouselCursor,
  hasMore = false
): CursorAdvance {
  if (deck.length === 0) return { kind: 'none' };

  const { conceptIdx, cardIdx } = cursor;

  if (cardIdx < cardCount(deck, conceptIdx) - 1) {
    return { kind: 'move', cursor: { conceptIdx, cardIdx: cardIdx + 1 } };
  }

  if (conceptIdx < deck.length - 1) {
    return { kind: 'move', cursor: { conceptIdx: conceptIdx + 1, cardIdx: 0 } };
  }

  // Last card of the last loaded concept.
  if (hasMore) return { kind: 'fetch', target: conceptIdx + 1 };

  return { kind: 'move', cursor: { conceptIdx: 0, cardIdx: 0 } };
}

/**
 * Where Previous goes from `cursor`.
 *
 * Always resolves — there is no "fetch backwards", because everything behind
 * the reader is by definition already loaded. Stepping back into a concept
 * lands on its LAST card, which is what makes Prev the exact inverse of Next.
 */
export function retreatCursor(
  deck: Deck,
  cursor: CarouselCursor
): CursorAdvance {
  if (deck.length === 0) return { kind: 'none' };

  const { conceptIdx, cardIdx } = cursor;

  if (cardIdx > 0) {
    return { kind: 'move', cursor: { conceptIdx, cardIdx: cardIdx - 1 } };
  }

  const prevIdx = conceptIdx > 0 ? conceptIdx - 1 : deck.length - 1;
  return {
    kind: 'move',
    cursor: {
      conceptIdx: prevIdx,
      // `Math.max(0, …)` guards a concept with no cards at all, which the feed
      // should never serve but which would otherwise produce `cardIdx: -1`.
      cardIdx: Math.max(0, cardCount(deck, prevIdx) - 1),
    },
  };
}

/**
 * Whether the reader is close enough to the loaded edge to warrant a silent
 * prefetch — §2.12 #15.
 *
 * Measured in CONCEPTS rather than cards, because a page boundary is crossed
 * per concept and a deck of single-card concepts would otherwise never trigger
 * far enough ahead.
 */
export const PREFETCH_THRESHOLD = 5;

export function shouldPrefetch(conceptIdx: number, loaded: number): boolean {
  return conceptIdx >= loaded - PREFETCH_THRESHOLD;
}
